/**
 * O dicionário do LinkFlow, do lado da tela (out/2026).
 *
 * Duas listas, uma decisão por palavra e por idioma: **aceitas** deixam de ser
 * acusadas pela ortografia (anglicismo e marca já nacionalizados — «mockup»,
 * «instagram»); **bloqueadas** passam a ser acusadas mesmo que o corretor as
 * conheça, com a correção que o administrador escrever. Por decisão do dono
 * (02/10/2026), só o administrador mexe e **cada palavra vale num idioma só**.
 *
 * O servidor aplica a lista na saída do Ler Texto e do Modo Texto; aqui mora o
 * que a tela decide: os idiomas e os rótulos, como ler o que foi colado na
 * caixa, o filtro da lista, o CSV e o desfecho de um «Aceitar». Puro, para
 * caber num `node --test`.
 */
import type { ArtOrganization } from "./api";
import type { DictionaryScope } from "../state/dictionaryScopeStore";

export type DictionaryKind = "accepted" | "blocked";
export type DictionaryLanguage = "pt" | "en" | "es" | "fr" | "it" | "de" | "nl" | "ar" | "ja" | "zh" | "ko";

export interface DictionaryEntry {
  id: string;
  kind: DictionaryKind;
  language: DictionaryLanguage;
  /** Como o administrador escreveu — a comparação ignora a caixa. */
  word: string;
  /** Só nas bloqueadas, e opcional. */
  correction: string | null;
  created_by: string | null;
  created_at: string | null;
  /** A empresa em que a palavra vale (21/10/2026); nula é o dicionário global. */
  organization_id?: string | null;
  organization_name?: string | null;
  /** `admin` ou `link` (o cliente aceitou pelo link); nula nas antigas. */
  created_via?: string | null;
}

export interface DictionaryList {
  items: DictionaryEntry[];
  total: number;
}

export interface DictionaryWordInput {
  word: string;
  correction?: string | null;
}

export interface DictionaryAddRequest {
  kind: DictionaryKind;
  language: DictionaryLanguage;
  entries: DictionaryWordInput[];
  /** O dicionário de uma empresa do cadastro; ausente é o global. */
  organization_id?: string | null;
}

export interface DictionarySkipped {
  word: string;
  /** `duplicate`: já está nesta lista; `conflict`: está na outra; `invalid`: não é palavra. */
  reason: "invalid" | "duplicate" | "conflict";
  detail: string;
}

export interface DictionaryAddResult {
  added: DictionaryEntry[];
  skipped: DictionarySkipped[];
}

/**
 * Os idiomas em que a ortografia do viewer roda: os do Ler Texto (com o árabe)
 * e os do Modo Texto (com o holandês); japonês, chinês e coreano estão nos
 * dois. O chinês é um só para as duas escritas. A mesma lista do backend (`IDIOMAS_DO_DICIONARIO`), na
 * mesma ordem.
 */
export const DICTIONARY_LANGUAGES: { code: DictionaryLanguage; label: string }[] = [
  { code: "pt", label: "Português" },
  { code: "en", label: "Inglês" },
  { code: "es", label: "Espanhol" },
  { code: "fr", label: "Francês" },
  { code: "it", label: "Italiano" },
  { code: "de", label: "Alemão" },
  { code: "nl", label: "Holandês" },
  { code: "ar", label: "Árabe" },
  { code: "ja", label: "Japonês" },
  { code: "zh", label: "Chinês" },
  { code: "ko", label: "Coreano" },
];

export const DICTIONARY_KIND_LABELS: Record<DictionaryKind, string> = {
  accepted: "Aceitas",
  blocked: "Bloqueadas",
};

/**
 * O idioma do dicionário para um código do corretor.
 *
 * O Ler Texto fala `pt-BR`, `en`, `ar`; o Modo Texto, `pt-BR`, `en-US`,
 * `de-DE`. O dicionário fala o código-base. Fora da lista → `null`: sem
 * idioma, não há o que aceitar.
 */
export function dictionaryLanguageOf(code: string | null | undefined): DictionaryLanguage | null {
  const base = String(code ?? "").trim().toLowerCase().split("-")[0];
  const conhecido = DICTIONARY_LANGUAGES.find((idioma) => idioma.code === base);
  return conhecido ? conhecido.code : null;
}

export function dictionaryLanguageLabel(code: string | null | undefined): string {
  const idioma = dictionaryLanguageOf(code);
  return DICTIONARY_LANGUAGES.find((item) => item.code === idioma)?.label ?? (code || "Idioma desconhecido");
}

/**
 * A forma em que as palavras se comparam: sem caixa, COM acento — a mesma
 * régua do servidor (`chave`), para a tela esconder exatamente o que ele
 * esconderia. O apóstrofo tipográfico vira o reto.
 */
export function dictionaryKey(word: string): string {
  return word.normalize("NFC").replace(/[’‘`´]/g, "'").toLowerCase();
}

// O separador entre a palavra bloqueada e a correção, numa linha colada.
const SEPARADOR_DA_CORRECAO = /\s*(?:->|→|=|\t|;|；)\s*/;

/**
 * O que foi colado na caixa da tela, como o pedido ao servidor.
 *
 * Aceitas: uma por linha — vírgula e ponto e vírgula também separam, porque
 * uma coluna colada de planilha ou uma lista de e-mail costuma vir assim.
 * Bloqueadas: uma por linha, a correção depois de «=», «→», «->», «;» ou
 * tabulação («email = e-mail»); sem separador, sem correção. A validação de
 * cada palavra é do servidor, que devolve o motivo de cada recusa.
 */
export function parseDictionaryInput(text: string, kind: DictionaryKind): DictionaryWordInput[] {
  const linhas = text.split(/\r?\n/).map((linha) => linha.trim()).filter(Boolean);
  if (kind === "accepted") {
    return linhas
      // «、», «，» e «；» são a vírgula e o ponto e vírgula do japonês e do chinês.
      .flatMap((linha) => linha.split(/[,;，；、]/))
      .map((palavra) => palavra.trim())
      .filter(Boolean)
      .map((word) => ({ word }));
  }
  return linhas.map((linha) => {
    const [word, ...resto] = linha.split(SEPARADOR_DA_CORRECAO);
    const correction = resto.join(" ").trim();
    return { word: word.trim(), correction: correction || null };
  });
}

export interface DictionaryFilters {
  kind: DictionaryKind;
  language: DictionaryLanguage | "";
  query: string;
}

function semAcento(texto: string): string {
  return texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/**
 * A lista que a tela mostra: a aba, o idioma e a busca — em ordem alfabética.
 *
 * A busca é a única coisa que ignora o acento: quem procura «cafe» quer ver
 * «café» também, mesmo que para o dicionário sejam duas palavras.
 */
export function filterDictionaryEntries(entries: DictionaryEntry[], filters: DictionaryFilters): DictionaryEntry[] {
  const busca = semAcento(filters.query.trim());
  return entries
    .filter((entry) => entry.kind === filters.kind)
    .filter((entry) => !filters.language || entry.language === filters.language)
    .filter((entry) => !busca || semAcento(entry.word).includes(busca) || semAcento(entry.correction ?? "").includes(busca))
    .sort((left, right) => {
      const porPalavra = left.word.localeCompare(right.word, "pt-BR", { sensitivity: "base" });
      if (porPalavra !== 0) return porPalavra;
      return dictionaryLanguageLabel(left.language).localeCompare(dictionaryLanguageLabel(right.language), "pt-BR");
    });
}

export function countByKind(entries: DictionaryEntry[]): Record<DictionaryKind, number> {
  return {
    accepted: entries.filter((entry) => entry.kind === "accepted").length,
    blocked: entries.filter((entry) => entry.kind === "blocked").length,
  };
}

function campoCsv(valor: string): string {
  return /[;"\r\n]/.test(valor) ? `"${valor.replace(/"/g, '""')}"` : valor;
}

/**
 * A lista em CSV, separada por «;» — o separador que o Excel em português
 * abre sem perguntar. Uma linha por palavra, nas duas listas.
 */
export function dictionaryCsv(entries: DictionaryEntry[]): string {
  const cabecalho = ["lista", "vale para", "idioma", "palavra", "correção", "adicionada por", "adicionada em"];
  const linhas = entries.map((entry) => [
    entry.kind === "accepted" ? "aceita" : "bloqueada",
    entry.organization_id ? (entry.organization_name ?? entry.organization_id) : "todas",
    dictionaryLanguageLabel(entry.language),
    entry.word,
    entry.correction ?? "",
    entry.created_by ?? "",
    entry.created_at ?? "",
  ]);
  return [cabecalho, ...linhas].map((linha) => linha.map(campoCsv).join(";")).join("\r\n") + "\r\n";
}

export interface DictionaryAddSummary {
  /** «2 palavras adicionadas.» */
  headline: string;
  /** Uma linha por palavra que não entrou, com o motivo. */
  details: string[];
}

export function addResultSummary(result: DictionaryAddResult): DictionaryAddSummary {
  const total = result.added.length;
  const headline = total === 0
    ? "Nenhuma palavra adicionada."
    : total === 1
      ? "1 palavra adicionada."
      : `${total} palavras adicionadas.`;
  return {
    headline,
    details: result.skipped.map((recusa) => `${recusa.word || "(vazia)"}: ${recusa.detail}`),
  };
}

/**
 * Quem vê o «aceitar» no viewer: SÓ o administrador, com sessão, no viewer
 * que o gerenciador abre (`/v/:slug`). Na public view, nunca — nem para o
 * administrador que abrir o link do cliente estando logado. Decisão do dono
 * (02/10/2026): uma palavra errada aceita por engano esconde o erro de todos.
 *
 * O compare (normal e público) nem monta o balão do Ler Texto nem o painel do
 * Modo Texto. E o servidor recusa a gravação de quem não for administrador de
 * sessão — esta regra é a da tela, não a única trava.
 */
export function canEditDictionaryInViewer(
  user: { is_admin?: boolean | null } | null | undefined,
  publicViewToken: string | null | undefined,
): boolean {
  return user?.is_admin === true && !publicViewToken;
}

/** Uma palavra aceita pelo botão do viewer, enquanto o balão ou o painel está aberto. */
export interface AcceptedWord {
  word: string;
  language: DictionaryLanguage;
  /**
   * O que o «Desfazer» apaga. `null` quando a palavra já estava na lista — e no
   * link do LinkFlow, onde não há desfazer: remover é do administrador.
   */
  entryId: string | null;
  /** Já estava no dicionário: a linha diz isso em vez de oferecer «desfazer». */
  already?: boolean;
  /** Para quem ela vale, quando não é o global: o nome da empresa. */
  scopeName?: string | null;
}

/**
 * Onde o «aceitar» grava, a partir do que a página sabe (21/10/2026).
 *
 * No link do LinkFlow, só com `tools.dictionary` e empresa — e aí grava no
 * dicionário dela. Fora do link, só o administrador; o administrador que abre
 * o link do cliente continua sem o botão (regra de 02/10/2026).
 */
export function dictionaryScopeFor(input: {
  publicViewToken: string | null | undefined;
  publicView?: { tools?: { dictionary?: boolean } | null; organization?: { name: string | null } | null } | null;
  isAdmin: boolean;
  artOrganization?: ArtOrganization | null;
}): DictionaryScope {
  if (input.publicViewToken) {
    const link = input.publicView;
    if (link?.tools?.dictionary === true && link.organization) {
      return { mode: "link", token: input.publicViewToken, organizationName: link.organization.name ?? null };
    }
    return { mode: "none" };
  }
  if (input.isAdmin) return { mode: "admin", artOrganization: input.artOrganization ?? null };
  return { mode: "none" };
}

/** O complemento do título do «aceitar»: onde a palavra deixa de ser acusada. */
export function acceptTargetLabel(scope: DictionaryScope): string {
  if (scope.mode === "link") return `nos arquivos de ${scope.organizationName ?? "sua empresa"}`;
  return "em todos os arquivos";
}

export type AcceptOutcome =
  | { kind: "accepted"; entryId: string }
  /** Já estava aceita (outra aba, ou a leitura veio de antes): só some da tela. */
  | { kind: "already" }
  /** Não entrou: está na lista de bloqueadas, ou não é uma palavra. */
  | { kind: "refused"; detail: string };

/** O desfecho de um «Aceitar» do viewer, a partir da resposta do servidor. */
export function acceptOutcome(result: DictionaryAddResult): AcceptOutcome {
  const [nova] = result.added;
  if (nova) return { kind: "accepted", entryId: nova.id };
  const [recusa] = result.skipped;
  if (recusa?.reason === "duplicate") return { kind: "already" };
  return { kind: "refused", detail: recusa?.detail ?? "A palavra não entrou no dicionário." };
}


// --- por organização (21/10/2026) -------------------------------------------

/** Uma linha da tela num escopo: a palavra, se é do global, e quem a sobrepõe. */
export interface ScopedDictionaryEntry {
  entry: DictionaryEntry;
  /** Palavra do global mostrada junto, no escopo de uma empresa. */
  global: boolean;
  /** A palavra da empresa que diz o contrário desta global — nos links dela, vale a dela. */
  overriddenBy: DictionaryEntry | null;
}

/**
 * As palavras de um escopo (`null` = global). Com `withGlobals`, numa empresa,
 * as globais vêm junto, depois das dela, marcadas — e a global que a empresa
 * contradiz traz quem a sobrepõe.
 */
export function entriesForScope(
  entries: DictionaryEntry[],
  scope: string | null,
  withGlobals: boolean,
): ScopedDictionaryEntry[] {
  const doEscopo = entries.filter((entry) => (entry.organization_id ?? null) === scope);
  const linhas: ScopedDictionaryEntry[] = doEscopo.map((entry) => ({ entry, global: false, overriddenBy: null }));
  if (scope !== null && withGlobals) {
    const daEmpresa = new Map(doEscopo.map((entry) => [`${entry.language}:${dictionaryKey(entry.word)}`, entry]));
    for (const entry of entries) {
      if (entry.organization_id) continue;
      const outra = daEmpresa.get(`${entry.language}:${dictionaryKey(entry.word)}`) ?? null;
      linhas.push({ entry, global: true, overriddenBy: outra && outra.kind !== entry.kind ? outra : null });
    }
  }
  return linhas;
}
